import type { ImageSizeType } from 'shared/components/image/shared';

export type MediaImageVersion = { width: number; height: number | null; url: string };
export type MediaImageProfile = ImageSizeType | 'square' | 'portrait';
export type MediaImageVersions = Partial<Record<MediaImageProfile, MediaImageVersion[]>>;
export type MediaImageSource = { url: string; image_versions?: MediaImageVersions | [] };
// Media delivery negotiates format by Accept; native image loaders would otherwise receive JPEG.
export const MEDIA_IMAGE_REQUEST_HEADERS = { Accept: 'image/webp,image/*;q=0.8' };

type VersionOptions = {
  sizeType?: ImageSizeType;
  aspectRatio?: number;
  maxSourceWidth?: number;
};

export function getMediaImageVersions(
  versions: MediaImageVersions | [] | null | undefined,
  { sizeType, aspectRatio, maxSourceWidth }: VersionOptions,
): MediaImageVersion[] {
  let profile: MediaImageProfile = sizeType ?? 'full';
  if (sizeType !== 'full' && sizeType !== 'avatar' && aspectRatio !== undefined) {
    if (Math.abs(aspectRatio - 1) < 0.01) profile = 'square';
    else if (Math.abs(aspectRatio - 3 / 4) < 0.01) profile = 'portrait';
    else if (Math.abs(aspectRatio - 4 / 3) < 0.01)
      profile = sizeType === 'large' ? 'large' : 'card';
    else return [];
  }

  if (!versions || Array.isArray(versions)) return [];
  const candidates = (versions[profile] ?? [])
    .filter((item) => Number.isFinite(item.width) && item.width > 0 && Boolean(item.url))
    .slice()
    .sort((a, b) => a.width - b.width)
    .filter((item, index, items) => index === 0 || item.width !== items[index - 1]?.width);
  if (!maxSourceWidth || !Number.isFinite(maxSourceWidth)) return candidates;
  const bounded = candidates.filter((item) => item.width <= maxSourceWidth);
  return bounded.length ? bounded : candidates.slice(0, 1);
}

export function selectMediaImageVersion(versions: MediaImageVersion[], width: number) {
  return versions.find((item) => item.width >= width) ?? versions[versions.length - 1];
}

export function buildMediaImageSources(versions: MediaImageVersion[], fallbackWidth: number) {
  const selected = selectMediaImageVersion(versions, fallbackWidth);
  if (!selected) return null;
  return {
    src: selected.url,
    // Full-size c_limit URLs specify maximum, not guaranteed, bitmap widths.
    srcSet: versions.every((item) => item.height !== null && item.height > 0)
      ? versions.map((item) => `${item.url} ${item.width}w`).join(', ')
      : undefined,
    preferred: null,
  };
}
